/**
 * Loads fax questionnaire state from Postgres into local UI state.
 *
 * Background prepare writes the questionnaire twice: once as `in_progress`
 * (empty answers) and again as `completed`/`error`. The accumulator snapshot
 * this page mounts with is therefore often the `in_progress` write, so the
 * poller re-reads the questionnaire from source on every tick rather than
 * re-inspecting that snapshot — otherwise the "being answered" state never
 * clears without a page refresh. Polling also covers landing here before the
 * first write exists (PG empty is a miss, not a Firestore fallback), and gives
 * up after a bounded wait so the user is never stuck on a spinner.
 *
 * @module procedureQuestionaire/hooks/useFaxQuestionnaireLoader
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { QuestionnaireStatus } from "../../../enums/questionnaireStatus";
import { logError } from "../../../utils/customLogger";
import { QuestionnaireState } from "../types";
import { loadFaxQuestionnaireDocument } from "../utils/faxQuestionnairePersistence";
import { fetchFaxQuestionnaireDocument } from "../utils/fetchFaxQuestionnaireDocument";

/** How often to re-read the questionnaire while AI prepare is still running. */
const POLL_INTERVAL_MS = 3000;

/** Give up on an `in_progress` questionnaire after this long. */
const IN_PROGRESS_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Give up waiting for prepare's very first write after this long. */
const AWAITING_FIRST_WRITE_TIMEOUT_MS = 60 * 1000;

interface UseFaxQuestionnaireLoaderParams {
  orderId: string | undefined;
  orgId: string | null | undefined;
  submissionDataLoading: boolean;
  pgQuestionnaireDetails: unknown;
}

/**
 * Whether a questionnaire status means prepare has finished writing.
 *
 * @param status - Status field from the questionnaire doc
 * @returns True when no further prepare write is expected
 */
const isTerminalStatus = (status: string | null): boolean =>
  status === QuestionnaireStatus.COMPLETED ||
  status === QuestionnaireStatus.ERROR;

/**
 * Fetches and exposes the fax questionnaire document for Procedure Q.
 *
 * @param params - Order/org ids plus submission accumulator fields
 * @returns Questionnaire snapshot, status, loading flag, and a local apply helper
 */
export const useFaxQuestionnaireLoader = (
  params: UseFaxQuestionnaireLoaderParams,
) => {
  const { orderId, orgId, submissionDataLoading, pgQuestionnaireDetails } =
    params;

  const [faxDataToSend, setFaxDataToSend] = useState<QuestionnaireState | null>(
    null,
  );
  const [isFaxDataLoading, setIsFaxDataLoading] = useState(false);
  const [questionnaireStatus, setQuestionnaireStatus] = useState<string | null>(
    null,
  );
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [hasPollTimedOut, setHasPollTimedOut] = useState(false);

  /* Guards the initial-load effect against a late accumulator snapshot
   * overwriting a newer, already-answered questionnaire from the poller.
   */
  const hasTerminalStatusRef = useRef(false);

  /**
   * Applies a questionnaire snapshot into local UI state.
   *
   * @param document - Full questionnaire doc, or null when missing
   */
  const applyQuestionnaireDocument = useCallback(
    (document: Record<string, unknown> | null) => {
      if (!document) {
        setFaxDataToSend(null);
        setQuestionnaireStatus(null);
        hasTerminalStatusRef.current = false;
        return;
      }
      const status =
        typeof document.status === "string" ? document.status : null;
      setFaxDataToSend(document as unknown as QuestionnaireState);
      setQuestionnaireStatus(status);
      hasTerminalStatusRef.current = isTerminalStatus(status);
    },
    [],
  );

  useEffect(() => {
    setHasLoadedOnce(false);
    setHasPollTimedOut(false);
    hasTerminalStatusRef.current = false;
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    if (submissionDataLoading) {
      setIsFaxDataLoading(true);
      return;
    }
    // The poller already has a finished questionnaire; a re-resolved snapshot
    // would only take the UI backwards.
    if (hasTerminalStatusRef.current) {
      setIsFaxDataLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsFaxDataLoading(true);
      try {
        const document = await loadFaxQuestionnaireDocument(
          orderId,
          pgQuestionnaireDetails,
          orgId,
        );
        if (!cancelled) {
          applyQuestionnaireDocument(document);
        }
      } catch (error) {
        logError(error as Error, "Error fetching form questions");
        if (!cancelled) {
          applyQuestionnaireDocument(null);
          setQuestionnaireStatus("error");
        }
      } finally {
        if (!cancelled) {
          setIsFaxDataLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    orderId,
    orgId,
    submissionDataLoading,
    pgQuestionnaireDetails,
    applyQuestionnaireDocument,
  ]);

  /* No doc at all yet: prepare was kicked off on form selection and has not
   * landed its first write, so keep looking instead of rendering an empty form.
   */
  const isAwaitingFirstWrite =
    questionnaireStatus === null && faxDataToSend === null;

  const shouldPoll =
    hasLoadedOnce &&
    !hasPollTimedOut &&
    !submissionDataLoading &&
    (questionnaireStatus === QuestionnaireStatus.IN_PROGRESS ||
      isAwaitingFirstWrite);

  useEffect(() => {
    if (!orderId || !shouldPoll) {
      return;
    }

    let cancelled = false;
    let intervalId: number | undefined;
    const deadline =
      Date.now() +
      (isAwaitingFirstWrite
        ? AWAITING_FIRST_WRITE_TIMEOUT_MS
        : IN_PROGRESS_POLL_TIMEOUT_MS);

    /**
     * Reads the questionnaire from source and applies it, or stops on timeout.
     *
     * Surfaces the last-known question tree when an `in_progress` prepare never
     * completes, so the reviewer can answer it manually instead of waiting.
     */
    const poll = async () => {
      if (Date.now() >= deadline) {
        window.clearInterval(intervalId);
        if (!cancelled) {
          setHasPollTimedOut(true);
          if (!isAwaitingFirstWrite) {
            setQuestionnaireStatus(QuestionnaireStatus.ERROR);
          }
        }
        return;
      }

      try {
        const document = await fetchFaxQuestionnaireDocument(orderId, orgId);
        if (!cancelled && document) {
          applyQuestionnaireDocument(document);
        }
      } catch (error) {
        logError(error as Error, "Error polling fax questionnaire status");
      }
    };

    // Fire once up front: prepare may already have completed while the page was
    // mounting, in which case the answers render without waiting a full tick.
    void poll();
    intervalId = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    orderId,
    orgId,
    shouldPoll,
    isAwaitingFirstWrite,
    applyQuestionnaireDocument,
  ]);

  /* While waiting on prepare's first write there is no status to report, but
   * the questionnaire is being answered all the same — report it as such so the
   * page shows the waiting panel and keeps Submit disabled.
   */
  const effectiveStatus =
    isAwaitingFirstWrite && shouldPoll
      ? QuestionnaireStatus.IN_PROGRESS
      : questionnaireStatus;

  return {
    faxDataToSend,
    isFaxDataLoading,
    questionnaireStatus: effectiveStatus,
    applyQuestionnaireDocument,
  };
};
